/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { bindAll, type UnbindFn } from 'bind-event-listener';

import { jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import type { ShortcutsConfig } from '@atlassian/global-side-navigation/types';

import {
	INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS,
	type InvitedAppShortcutAllowedApsKey,
	type InvitedAppShortcutAllowedTargetAppKey,
	type InvitedAppShortcutCollaborator,
} from '../../../common/types';
import {
	getAppKeyByApsKey,
	isAllowedApsKey,
} from '../../../common/utils/invited-app-shortcuts-utils';
import { InvitedAppShortcutsContext } from '../../../controllers/invited-app-shortcuts-context';
import type { UseInvitedAppShortcutsResult } from '../../../services/invited-app-shortcuts';
import { useInvitedAppAnalytics } from '../../../services/invited-app-shortcuts/invited-app-analytics';
import type { InvitedAppDetails } from '../../../services/invited-app-shortcuts/types';

import { InvitedAppShortcutLabel } from './invited-app-shortcut-label';
import { LazyInvitedAppPopup } from './lazy-invited-app-popup';

type ErrorBoundaryProps = {
	children: React.ReactNode;
	fallback: (renderProps: { referrableShortcuts: ShortcutsConfig }) => React.ReactNode;
	fireInvitedAppShortcutsContainerRenderFailed: (
		params: Parameters<
			ReturnType<typeof useInvitedAppAnalytics>['fireInvitedAppShortcutsContainerRenderFailed']
		>[0],
	) => void;
	shortcutsConfig: ShortcutsConfig;
};

// eslint-disable-next-line @repo/internal/react/no-class-components -- using a class component for error boundary
class ErrorBoundary extends React.Component<ErrorBoundaryProps, { hasError: boolean }> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error: Error, _errorInfo: React.ErrorInfo) {
		this.props.fireInvitedAppShortcutsContainerRenderFailed({
			error: error.message,
			errorStack: error.stack,
			targetApps: Object.values(this.props.shortcutsConfig)
				.map((shortcut) => shortcut.analyticsName)
				.filter(Boolean) as string[],
		});
	}

	render() {
		if (this.state.hasError) {
			return this.props.fallback({ referrableShortcuts: this.props.shortcutsConfig });
		}

		return this.props.children;
	}
}

export interface InvitedAppShortcutsContainerProps {
	children: (renderProps: { referrableShortcuts: ShortcutsConfig }) => React.ReactNode;
	/**
	 * The number of shortcuts from the end to exclude from getting refs.
	 * For example, if there are 5 shortcuts and excludeLastFromRefs is 2,
	 * only the first 3 shortcuts will get refs.
	 */
	excludeLastFromRefs?: number;
	hoverDelayTimeMs?: number;

	// TODO(PUMA-1269): make this required:
	resolverResult?: UseInvitedAppShortcutsResult;

	shortcutsConfig: ShortcutsConfig;
}

const InvitedAppShortcutsContainerInner = ({
	shortcutsConfig,
	resolverResult,
	hoverDelayTimeMs = 300,
	excludeLastFromRefs = 0,
	children,
}: InvitedAppShortcutsContainerProps) => {
	const { fireViewedInvitedAppShortcuts } = useInvitedAppAnalytics();

	// the popup shown state
	const [isPopupShown, setIsPopupShown] = useState(false);
	const closePopupTimerRef = useRef<NodeJS.Timeout | null>(null);

	// refs for the shortcut items
	const jiraShortcutRef = useRef<HTMLAnchorElement | null>(null);
	const confluenceShortcutRef = useRef<HTMLAnchorElement | null>(null);

	// Store unbind functions for cleanup
	const unbindFunctionsRef = useRef<
		Map<React.MutableRefObject<HTMLAnchorElement | null>, UnbindFn>
	>(new Map());

	// Ref for the current shortcut item
	// the current shortcut item is unset when the popup is hidden after a delay
	const [currentShortcutRef, setCurrentShortcutRef] =
		useState<React.MutableRefObject<HTMLAnchorElement | null>>();

	// map of the shortcut items to their refs
	const shortcutRefs: Record<
		InvitedAppShortcutAllowedApsKey,
		React.MutableRefObject<HTMLAnchorElement | null>
	> = useMemo(
		() => ({
			JIRA_SOFTWARE: jiraShortcutRef,
			CONFLUENCE: confluenceShortcutRef,
		}),
		// Refs are stable, no dependencies needed
		[],
	);

	const showPopup = useCallback(() => {
		setIsPopupShown(true);
	}, [setIsPopupShown]);

	const hidePopup = useCallback(() => {
		setIsPopupShown(false);
		setCurrentShortcutRef(undefined);
	}, [setIsPopupShown, setCurrentShortcutRef]);

	const clearClosePopupTimer = useCallback(() => {
		if (closePopupTimerRef.current) {
			clearTimeout(closePopupTimerRef.current);
			closePopupTimerRef.current = null;
		}
	}, []);

	const startClosePopupTimer = useCallback(() => {
		clearClosePopupTimer();
		closePopupTimerRef.current = setTimeout(() => {
			hidePopup();
		}, hoverDelayTimeMs);
	}, [clearClosePopupTimer, hoverDelayTimeMs, hidePopup]);

	/**
	 * @param targetRef - the ref to the shortcut item
	 * @returns a handler for the mouse over event on the shortcut item
	 */
	const handleMouseOverWithRef = useCallback(
		(targetRef: React.MutableRefObject<HTMLAnchorElement | null>) => () => {
			// clear the close popup timer to avoid closing the popup
			// when moving the mouse from the popup to the shortcut item
			clearClosePopupTimer();

			setCurrentShortcutRef(targetRef);

			showPopup();
		},
		[clearClosePopupTimer, showPopup],
	);

	/**
	 * @param targetRef - the ref to the shortcut item
	 * @returns a handler for the mouse leave event on the shortcut item
	 */
	const handleMouseLeaveWithRef = useCallback(
		() => () => {
			if (currentShortcutRef?.current) {
				currentShortcutRef.current.style.backgroundColor = '';
			}

			startClosePopupTimer();
		},
		[currentShortcutRef, startClosePopupTimer],
	);

	/**
	 * @param targetRef - the ref to the shortcut item
	 * @returns a callback ref for a shortcut item
	 */
	const createShortcutRef = useCallback(
		(targetRef: React.MutableRefObject<HTMLAnchorElement | null>) =>
			(node: HTMLAnchorElement | null) => {
				// Cleanup previous element if it exists
				const existingUnbind = unbindFunctionsRef.current.get(targetRef);
				if (existingUnbind) {
					existingUnbind();
					unbindFunctionsRef.current.delete(targetRef);
				}

				// Store the new element
				// oxlint-disable-next-line no-param-reassign
				targetRef.current = node;

				// Attach listeners to new element
				if (node) {
					const mouseoverListener = handleMouseOverWithRef(targetRef);
					const focusListener = handleMouseOverWithRef(targetRef);
					const mouseleaveListener = handleMouseLeaveWithRef();
					const blurListener = handleMouseLeaveWithRef();

					// Use bind-event-listener to bind all events and get an unbind function
					const unbind = bindAll(node, [
						{
							type: 'mouseover',
							listener: mouseoverListener,
						},
						{
							type: 'focus',
							listener: focusListener,
						},
						{
							type: 'mouseleave',
							listener: mouseleaveListener,
						},
						{
							type: 'blur',
							listener: blurListener,
						},
					]);

					// Store the unbind function for cleanup
					unbindFunctionsRef.current.set(targetRef, unbind);
				}
			},
		[handleMouseOverWithRef, handleMouseLeaveWithRef],
	);

	/**
	 * Handles the popup shown event
	 * @returns a handler for the popup shown event
	 */
	const handleOnMouseEnterPopup = useCallback(() => {
		// clear the close popup timer to avoid closing the popup too early
		clearClosePopupTimer();

		if (currentShortcutRef?.current) {
			// Get the parent element of the shortcut item
			const { parentElement } = currentShortcutRef.current;

			if (parentElement) {
				// reference the background color variable from the design system
				// https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/4ff76dcfb3ce12b4344e5345be748ce472ac56e7/platform/packages/design-system/navigation-system/src/ui/menu-item/menu-item.tsx#lines-166
				parentElement.style.backgroundColor = token('elevation.surface.hovered');
			}
		}

		// ensure the popup is shown when mouse leaves the shortcut item and over the popup
		showPopup();
	}, [clearClosePopupTimer, showPopup, currentShortcutRef]);

	/**
	 * Handles the popup closed event
	 * @returns a handler for the popup closed event
	 */
	const handleOnMouseLeavePopup = useCallback(() => {
		if (currentShortcutRef?.current) {
			// Get the parent element of the shortcut item
			const { parentElement } = currentShortcutRef.current;

			if (parentElement) {
				parentElement.style.backgroundColor = '';
			}
		}
		startClosePopupTimer();
	}, [currentShortcutRef, startClosePopupTimer]);

	const referrableShortcuts: ShortcutsConfig = useMemo(() => {
		const entries = Object.entries(shortcutsConfig);
		const max = Math.min(
			entries.length,
			resolverResult
				? Object.values(resolverResult.invitedApps ?? {}).filter((app) => app?.eligible).length
				: // TODO(PUMA-1269) Cleanup this fallback once resolverResult is always provided:
					INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS.length,
		);
		const refsLimit = max - excludeLastFromRefs;
		let createdRefsCount = 0;

		return entries.reduce((acc, [key, shortcut]) => {
			// only add ref to shortcuts that are within the refs limit
			// and if it is a link and the aps key is allowed
			if (
				createdRefsCount < refsLimit &&
				shortcut.type === 'link' &&
				shortcut.apsKey &&
				(resolverResult
					? resolverResult.invitedApps?.[
							getAppKeyByApsKey(shortcut.apsKey as InvitedAppShortcutAllowedApsKey)
						]?.eligible
					: // TODO(PUMA-1269) Cleanup this fallback once resolverResult is always provided:
						INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS.includes(
							shortcut.apsKey as InvitedAppShortcutAllowedApsKey,
						))
			) {
				const apsKey = shortcut.apsKey as InvitedAppShortcutAllowedApsKey;
				const app = getAppKeyByApsKey(apsKey);
				const targetRef = shortcutRefs[apsKey];
				const originalOnClick = shortcut.onClick;
				acc[key] = {
					...shortcut,
					onClick: (evt) => {
						resolverResult?.registerActivation(app);
						return originalOnClick?.(evt);
					},
					ref: targetRef ? createShortcutRef(targetRef) : undefined,
					labelElem: <InvitedAppShortcutLabel apsKey={apsKey} label={shortcut.label} />,
				};

				// oxlint-disable-next-line no-plusplus
				createdRefsCount++;
			} else {
				acc[key] = shortcut;
			}

			return acc;
		}, {} as ShortcutsConfig);
	}, [shortcutsConfig, resolverResult, createShortcutRef, shortcutRefs, excludeLastFromRefs]);

	useEffect(() => {
		const currentUnbindFunctions = unbindFunctionsRef.current;
		const currentShortcutRefs = shortcutRefs;

		return () => {
			// Clean up all event listeners using the stored unbind functions
			Object.values(currentShortcutRefs).forEach((ref) => {
				const unbind = currentUnbindFunctions.get(ref);
				if (unbind) {
					unbind();
					currentUnbindFunctions.delete(ref);
				}
			});

			clearClosePopupTimer();
		};
	}, [clearClosePopupTimer, shortcutRefs]);

	const currentApsKey = useMemo(
		() =>
			Object.keys(shortcutRefs).find(
				(apsKey) =>
					currentShortcutRef?.current ===
					shortcutRefs[apsKey as InvitedAppShortcutAllowedApsKey]?.current,
			) as InvitedAppShortcutAllowedApsKey | undefined,
		[shortcutRefs, currentShortcutRef],
	);

	const currentAppKey = currentApsKey && getAppKeyByApsKey(currentApsKey);
	const currentAppResolved = currentAppKey && resolverResult?.invitedApps?.[currentAppKey];

	const currentAppCollaborators = useMemo<InvitedAppShortcutCollaborator[]>(
		() =>
			currentAppResolved?.collaborators?.map((collab) => ({
				name: '',
				src: collab?.avatar,
			})) ?? [],
		[currentAppResolved?.collaborators],
	);

	const currentShortcutLink = useMemo(
		() => currentShortcutRef?.current?.href,
		[currentShortcutRef],
	);

	useEffect(() => {
		// These are the app keys for which we'll be displaying our treatment
		const invitedAppShortcutKeys: InvitedAppShortcutAllowedTargetAppKey[] = Object.values(
			referrableShortcuts,
		).reduce((acc, shortcut) => {
			if (shortcut.type === 'link' && isAllowedApsKey(shortcut.apsKey) && !!shortcut.ref) {
				acc.push(getAppKeyByApsKey(shortcut.apsKey));
			}
			return acc;
		}, [] as InvitedAppShortcutAllowedTargetAppKey[]);

		resolverResult?.registerImpression({ products: invitedAppShortcutKeys });

		// Fire an analytics event when the user views the invited app shortcuts
		if (invitedAppShortcutKeys.length > 0) {
			fireViewedInvitedAppShortcuts(invitedAppShortcutKeys);
		}
	}, [referrableShortcuts, resolverResult, fireViewedInvitedAppShortcuts]);

	return (
		<InvitedAppShortcutsContext.Provider
			value={{
				impressedProducts: {
					JIRA_SOFTWARE: isAppImpressed(resolverResult?.invitedApps?.jira),
					CONFLUENCE: isAppImpressed(resolverResult?.invitedApps?.confluence),
				},
			}}
		>
			{children({
				referrableShortcuts,
			})}
			{isPopupShown && currentShortcutRef && currentShortcutLink && currentApsKey && (
				<LazyInvitedAppPopup
					trigger={currentShortcutRef}
					onMouseEnterPopup={handleOnMouseEnterPopup}
					onMouseLeavePopup={handleOnMouseLeavePopup}
					currentApsKey={currentApsKey}
					currentAppCollaborators={currentAppCollaborators}
					currentShortcutLink={currentShortcutLink}
					resolverResult={resolverResult}
				/>
			)}
		</InvitedAppShortcutsContext.Provider>
	);
};

export const InvitedAppShortcutsContainer = (props: InvitedAppShortcutsContainerProps) => {
	const { fireInvitedAppShortcutsContainerRenderFailed } = useInvitedAppAnalytics();

	return (
		<ErrorBoundary
			fallback={props.children}
			shortcutsConfig={props.shortcutsConfig}
			fireInvitedAppShortcutsContainerRenderFailed={fireInvitedAppShortcutsContainerRenderFailed}
		>
			<InvitedAppShortcutsContainerInner {...props} />
		</ErrorBoundary>
	);
};

const isAppImpressed = (appDetails: InvitedAppDetails | undefined) =>
	!!appDetails?.firstImpressionAt;
